import React, { useContext, useEffect, useState } from "react";
import { Redirect } from "react-router";

import CodiaxHeader from "../CodiaxHeader/CodiaxHeader";

import CodiaxContext from "../CodiaxContext";
import CodiaxDataTable from "./CodiaxDataTable";
import CodiaxPOAPError from "./error/CodiaxPOAPError";
import CodiaxEmptyData from "./empty/CodiaxEmptyData";
import CodeEditor from "@uiw/react-textarea-code-editor";

import CODIAX_DATA from "../assets/data/codiax-data";
import {
	HASHES_CONTRACT,
	BRIDGE_CONTRACT,
	MARKETPLACE_URL,
	COMPUTE_TO_DATA_SCRIPT,
	NOTEBOOK_URL,
} from "../Config";

import s from "./CodiaxDataPage.module.scss";
import { Button } from "reactstrap";
import cx from "classnames";
import AppConfig from "../../../../AppConfig";

const USDC_DECIMALS = 6;

const INSTALL_CODE = `mkdir -p ~/wevnode
cd ~/wevnode
wget https://codiaxnode.weavechain.com/file/install.sh
chmod a+x install.sh
./install.sh

#jupyter server
wget https://codiaxnode.weavechain.com/file/install_jupyter.sh
chmod a+x install_jupyter.sh
./install_jupyter.sh`;

const DEFAULT_WRITE_CODE = `const record = [
	null,               	//id, filled server side
	"Johnny",         	// name_nickname
	"Doe",        		// name_last
	"John",         	// name_first
	"johndoe@email.com", 	// email
	"+123456789",       	// phone - erasure applied at read
	"USA",              	// address_country
	"San Francisco",    	// address_city - erasure applied at read
	"https://linkedin.com/in/test", //  linkedin_url
	"name#1234",        	// discord_username
	"@test",            	// telegram username
	"0x1234",           	// wallet - erasure applied at read
	"1990-01-01",       	// birthday, YYYY-MM-DD format - erasure applied at read
];
const records = new Records(table, [ record ]);
nodeApi.write(session, collection, records, WRITE_DEFAULT_OPTIONS)`;

const DEFAULT_COMPUTE_MPC_CODE = `const algo = "mean";
const filter = new Filter(FilterOp.gte("birthday", 0), {"id": "ASC"}, null, [ "email"]);
nodeApi.mpc(session, collection, table, algo, [ "birthday" ], filter, buildMPCOptions(MPC_DEFAULT_OPTIONS, [ "weavej6LRTW47f6yFt2riACi5VyhD74CZEYhyqVBSR755FETP" ]))`;

const DEFAULT_COMPUTE_TO_DATA_CODE = `nodeApi.compute(session, "gcr.io/weavechain/sample_xgboost", COMPUTE_DEFAULT_OPTIONS)`;

export default function CodiaxDataPage() {
	const [writeCode, setWriteCode] = useState(DEFAULT_WRITE_CODE);
	const [computeMPCCode, setComputeMPCCode] = useState(
		DEFAULT_COMPUTE_MPC_CODE
	);
	const [computeToDataCode, setComputeToDataCode] = useState(
		DEFAULT_COMPUTE_TO_DATA_CODE
	);
	const [computeToDataResult, setComputeToDataResult] = useState("Unavailable");
	const [data, setData] = useState(null);
	const [hasPoap, setHasPoap] = useState(false);
	const [step, setStep] = useState(1);
	const [averageAge, setAverageAge] = useState(null);
	const [balance, setBalance] = useState(null);
	const { user, hasAccess, weave } = useContext(CodiaxContext) || {};

	useEffect(() => {
		setHasPoap(user?.metamaskAccount && hasAccess);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	// No metamask account
	if (!user) return <Redirect to="/" />;

	// ------------------------------------- METHODS -------------------------------------
	const writeData = async () => {
		weave.writeCode(writeCode).then((r) => {
			getData();
			setStep(step + 1);
		});
	};

	const computeMPC = async () => {
		weave.computeCode(computeMPCCode).then((r) => {
			console.log(r);
			const result = r?.data;

			if (result) {
				const avgDate = new Date(result * 1);
				const diff = new Date() - avgDate;
				const days = diff / (24 * 3600 * 1000);
				const years = Math.round((100 * days) / 365.25) / 100; //new Date(diff).getFullYear() - 1970;
				console.log("Average age", years);
				setAverageAge(years);
			}
		});
	};

	const computeToData = async () => {
		weave.computeCode(computeToDataCode).then((r) => {
			const data = r?.data;
			if (data) {
				const toShow =
					"Signature: " +
					data["outputSignature"] +
					"\n\nInputs Hash:" +
					data["inputHash"] +
					"\nCompute Hash:" +
					data["computeHash"] +
					"\nOutput Hash:" +
					data["outputHash"] +
					"\n\nOutput:" +
					data["output"];
				setComputeToDataResult(toShow);
			}
		});
	};

	const compute = async () => {
		const result = await weave.computeAvg("birthday");
		console.log(result);

		const avgDate = new Date(result * 1);
		const diff = new Date() - avgDate;
		const days = diff / (24 * 3600 * 1000);
		const years = days / 365.25; //new Date(diff).getFullYear() - 1970;
		console.log("Average age", Math.round(years * 100) / 100);
		setAverageAge(years);
	};

	const download = async () => {
		weave.download();
	};

	const readBalance = async () => {
		const usdc = await weave.balance();
		setBalance(usdc / Math.pow(10, USDC_DECIMALS));
	};

	const withdraw = async () => {
		try {
			const res = await weave.withdraw();
			console.log(res);
		} catch (e) {
			console.log(e);
		}
	};

	const next = async () => {
		setStep(step + 1);
	};

	const prev = async () => {
		setStep(Math.max(1, step - 1));
	};

	const getData = async () => {
		if (false) {
			//mocked
			setData(CODIAX_DATA);
		} else {
			try {
				const { data, layout, hashes } = await weave.read();

				if (data.length === 0) {
					const values = [];
					const record = [];
					layout.columnNames.forEach((v) => (record[v] = ""));
					values.push(record);
					setData(values);
				} else {
					setData(data);
				}
			} catch (e) {
				//pass
			}
		}
	};

	return (
		<div className={s.root}>
			<CodiaxHeader showHelp />

			<div className={s.content}>
				<div className={s.infoContainer}>
					<p className={s.title}>
						welcome to the CODIAX 2022 Weavechain Workshop
					</p>
				</div>

				{data ? (
					step === 1 ? (
						<>
							<p>&nbsp;</p>
							<Button
								className={cx(s.button, "btn-square dark")}
								color="primary"
								onClick={() => {
									next();
								}}
							>
								>
							</Button>

							<CodiaxDataTable data={data} />
						</>
					) : step === 2 ? (
						<>
							<p>&nbsp;</p>
							<Button
								className={cx(s.button, "btn-square dark")}
								color="primary"
								onClick={() => {
									prev();
								}}
							>
								&#60;
							</Button>
							&nbsp;
							<Button
								className={cx(s.button, "btn-small dark")}
								color="primary"
								onClick={() => {
									writeData();
								}}
							>
								Write >
							</Button>
							&nbsp;
							<Button
								className={cx(s.button, "btn-square dark")}
								color="primary"
								onClick={() => {
									next();
								}}
							>
								>
							</Button>
							<CodeEditor
								value={writeCode}
								language="js"
								placeholder="Please enter JS code."
								onChange={(evn) => setWriteCode(evn.target.value)}
								padding={15}
								style={{
									fontSize: 12,
									backgroundColor: "#f5f5f5",
									fontFamily:
										"ui-monospace,SFMono-Regular,SF Mono,Consolas,Liberation Mono,Menlo,monospace",
								}}
							/>
						</>
					) : step === 3 ? (
						<>
							<p>&nbsp;</p>
							<Button
								className={cx(s.button, "btn-square dark")}
								color="primary"
								onClick={() => {
									prev();
								}}
							>
								&#60;
							</Button>
							&nbsp;
							<Button
								className={cx(s.button, "btn-small dark")}
								color="primary"
								onClick={() => {
									window.open(HASHES_CONTRACT, "_blank");
								}}
							>
								Hashes Contract
							</Button>
							&nbsp;
							<Button
								className={cx(s.button, "btn-square dark")}
								color="primary"
								onClick={() => {
									next();
								}}
							>
								>
							</Button>
							<CodiaxDataTable data={data} />
							<p>&nbsp;</p>
							<Button
								className={cx(s.button, "btn-small dark")}
								color="primary"
								onClick={() => {
									getData();
								}}
							>
								Refresh
							</Button>
						</>
					) : step === 4 ? (
						<>
							<p>&nbsp;</p>
							<Button
								className={cx(s.button, "btn-square dark")}
								color="primary"
								onClick={() => {
									prev();
								}}
							>
								&#60;
							</Button>
							&nbsp;
							<Button
								className={cx(s.button, "btn-small dark")}
								color="primary"
								onClick={() => {
									readBalance();
								}}
							>
								Balance
							</Button>
							&nbsp;
							<Button
								className={cx(s.button, "btn-small dark")}
								color="primary"
								onClick={() => {
									window.open(BRIDGE_CONTRACT, "_blank");
								}}
							>
								Bridge Contract
							</Button>
							{/*&nbsp;
							<Button
								className={cx(s.button, "btn-small dark")}
								color="primary"
								onClick={() => {
									withdraw()
								}}
							>
								Withdraw
							</Button>*/}
							&nbsp;
							<Button
								className={cx(s.button, "btn-square dark")}
								color="primary"
								onClick={() => {
									next();
								}}
							>
								>
							</Button>
							<p>&nbsp;</p>
							<p>&nbsp;</p>
							<div className={s.infoContainer}>
								<p className={s.subtitle}>Balance: {balance}</p>
							</div>
						</>
					) : step === 5 ? (
						<>
							<p>&nbsp;</p>
							<Button
								className={cx(s.button, "btn-square dark")}
								color="primary"
								onClick={() => {
									prev();
								}}
							>
								&#60;
							</Button>
							&nbsp;
							<Button
								className={cx(s.button, "btn-small dark")}
								color="primary"
								onClick={() => {
									download();
								}}
							>
								Download
							</Button>
							&nbsp;
							<Button
								className={cx(s.button, "btn-small dark")}
								color="primary"
								onClick={() => {
									window.open(MARKETPLACE_URL, "_blank");
								}}
							>
								Marketplace
							</Button>
							&nbsp;
							<Button
								className={cx(s.button, "btn-square dark")}
								color="primary"
								onClick={() => {
									next();
								}}
							>
								>
							</Button>
							<p>&nbsp;</p>
							<div className={s.infoContainer}>
								<p className={s.subtitle}>Faucets</p>
							</div>
							<div>
								<a
									href={"https://calibration-faucet.filswan.com/"}
									target={"_blank"}
									rel="noreferrer"
								>
									https://calibration-faucet.filswan.com/
								</a>
							</div>
							<div>
								<a href={"https://mumbaifaucet.com/"} target={"_blank"}>
									https://mumbaifaucet.com/
								</a>
							</div>
						</>
					) : step === 6 ? (
						<>
							<p>&nbsp;</p>
							<Button
								className={cx(s.button, "btn-square dark")}
								color="primary"
								onClick={() => {
									prev();
								}}
							>
								&#60;
							</Button>
							&nbsp;
							<Button
								className={cx(s.button, "btn-small dark")}
								color="primary"
								onClick={() => {
									computeMPC();
								}}
							>
								Compute
							</Button>
							&nbsp;
							<Button
								className={cx(s.button, "btn-square dark")}
								color="primary"
								onClick={() => {
									next();
								}}
							>
								>
							</Button>
							<CodeEditor
								value={computeMPCCode}
								language="js"
								placeholder="Please enter JS code."
								onChange={(evn) => setComputeMPCCode(evn.target.value)}
								padding={15}
								style={{
									fontSize: 12,
									backgroundColor: "#f5f5f5",
									fontFamily:
										"ui-monospace,SFMono-Regular,SF Mono,Consolas,Liberation Mono,Menlo,monospace",
								}}
							/>
							<p>&nbsp;</p>
							<p>&nbsp;</p>
							<div className={s.infoContainer}>
								<p className={s.subtitle}>Average age: {averageAge}</p>
							</div>
						</>
					) : step === 7 ? (
						<>
							<p>&nbsp;</p>
							<Button
								className={cx(s.button, "btn-square dark")}
								color="primary"
								onClick={() => {
									prev();
								}}
							>
								&#60;
							</Button>
							&nbsp;
							<Button
								className={cx(s.button, "btn-small dark")}
								color="primary"
								onClick={() => {
									computeToData();
								}}
							>
								Compute To Data
							</Button>
							&nbsp;
							<Button
								className={cx(s.button, "btn-small dark")}
								color="primary"
								onClick={() => {
									window.open(NOTEBOOK_URL, "_blank");
								}}
							>
								Jupyter Notebook
							</Button>
							&nbsp;
							<Button
								className={cx(s.button, "btn-square dark")}
								color="primary"
								onClick={() => {
									next();
								}}
							>
								>
							</Button>
							<CodeEditor
								value={computeToDataCode}
								language="js"
								placeholder="Please enter JS code."
								onChange={(evn) => setComputeToDataCode(evn.target.value)}
								padding={15}
								style={{
									fontSize: 12,
									backgroundColor: "#f5f5f5",
									fontFamily:
										"ui-monospace,SFMono-Regular,SF Mono,Consolas,Liberation Mono,Menlo,monospace",
								}}
							/>
							<p>&nbsp;</p>
							<div className={s.infoContainer}>
								<p className={s.subtitle}>Result</p>
							</div>
							<CodeEditor
								value={computeToDataResult}
								language="js"
								rows={20}
								placeholder=""
								onChange={(evn) => {}}
								padding={15}
								style={{
									fontSize: 12,
									backgroundColor: "#f5f5f5",
									fontFamily:
										"ui-monospace,SFMono-Regular,SF Mono,Consolas,Liberation Mono,Menlo,monospace",
								}}
							/>
							<p>&nbsp;</p>
							<div className={s.infoContainer}>
								<p className={s.subtitle}>Remotely executed code</p>
							</div>
							<CodeEditor
								value={COMPUTE_TO_DATA_SCRIPT}
								language="js"
								rows={20}
								placeholder=""
								onChange={(evn) => {}}
								padding={15}
								style={{
									fontSize: 12,
									backgroundColor: "#f5f5f5",
									fontFamily:
										"ui-monospace,SFMono-Regular,SF Mono,Consolas,Liberation Mono,Menlo,monospace",
								}}
							/>
						</>
					) : (
						<>
							<p>&nbsp;</p>
							<p>&nbsp;</p>
							<Button
								className={cx(s.button, "btn-square dark")}
								color="primary"
								onClick={() => {
									prev();
								}}
							>
								&#60;
							</Button>
							&nbsp;
							<p>&nbsp;</p>
							<Button
								className={cx(s.button, "btn-border dark")}
								color="primary"
								onClick={() =>
									window.open(AppConfig.INSTALLATION_URL, "_blank")
								}
							>
								How to Install a Weavechain Node
							</Button>
							<p>&nbsp;</p>
							<div className={s.infoContainer}>
								<p className={s.subtitle}>Linux/MacOS</p>
							</div>
							<CodeEditor
								value={INSTALL_CODE}
								language="js"
								placeholder=""
								onChange={(evn) => {}}
								padding={15}
								style={{
									fontSize: 12,
									backgroundColor: "#f5f5f5",
									fontFamily:
										"ui-monospace,SFMono-Regular,SF Mono,Consolas,Liberation Mono,Menlo,monospace",
								}}
							/>
							<p>&nbsp;</p>
							<div className={s.infoContainer}>
								<p className={s.subtitle}>Windows</p>
							</div>
							<p style={{ color: "white" }}>
								Create a new folder and copy{" "}
								<a
									href={"https://codiaxnode.weavechain.com/file/install.bat"}
									download
									target={"_blank"}
									rel="noreferrer"
								>
									install.bat
								</a>
								, run it after adding the folder in Settings -> Resources ->
								File Sharing in Docker Desktop
							</p>
							<p style={{ color: "white" }}>
								Then run{" "}
								<a
									href={
										"https://codiaxnode.weavechain.com/file/install_jupyter.bat"
									}
									download
									target={"_blank"}
									rel="noreferrer"
								>
									install_jupyter.bat
								</a>{" "}
								in the same folder
							</p>
						</>
					)
				) : !hasPoap ? (
					<CodiaxPOAPError readData={getData} />
				) : (
					<CodiaxEmptyData readData={getData} />
				)}
			</div>
		</div>
	);
}
